import { useState } from 'react';
import { DAYS_ABBREVIATIONS } from '../../../constants/weekDayConstants';
import { TrashCanIcon, PlusIcon } from '../../../assets/svgs/icons';

export interface HoursEntry {
  days: boolean[];
  startTime: string;
  endTime: string;
}

export interface HoursData {
  entries: HoursEntry[];
}

interface LocationHoursStepProps {
  data: HoursData;
  onChange: (data: HoursData) => void;
  onNext: () => void;
  onBack: () => void;
}

type HoursEntryErrors = {
  days?: string;
  startTime?: string;
  endTime?: string;
};

const DAYS = Object.keys(DAYS_ABBREVIATIONS);

const parseTime = (value: string): number | null => {
  const match = value.trim().match(/^(\d{1,2})(?::([0-5]\d))?\s*(AM|PM)$/i);

  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? '0');
  const period = match[3].toUpperCase();

  if (hours < 1 || hours > 12) return null;

  return ((hours % 12) + (period === 'PM' ? 12 : 0)) * 60 + minutes;
};

const validateEntry = (entry: HoursEntry): HoursEntryErrors => {
  const errors: HoursEntryErrors = {};

  if (!entry.days.some(Boolean)) {
    errors.days = 'Select at least one day.';
  }

  const start = entry.startTime.trim();
  const end = entry.endTime.trim();
  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);

  if (!start) {
    errors.startTime = 'Start time is required.';
  } else if (startMinutes === null) {
    errors.startTime = 'Enter a valid time, e.g. 7:00 AM.';
  }

  if (!end) {
    errors.endTime = 'End time is required.';
  } else if (endMinutes === null) {
    errors.endTime = 'Enter a valid time, e.g. 10:00 PM.';
  }

  if (startMinutes !== null && endMinutes !== null && startMinutes === endMinutes) {
    errors.endTime = 'End time must differ from start time.';
  }

  return errors;
};

const emptyEntry = (): HoursEntry => ({
  days: Object.values(DAYS_ABBREVIATIONS),
  startTime: '',
  endTime: ''
});

const LocationHoursStep = ({ data, onChange, onNext, onBack }: LocationHoursStepProps) => {
  const [errors, setErrors] = useState<HoursEntryErrors[]>([]);
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);

  const handleNext = () => {
    setHasAttemptedNext(true);

    const nextErrors = data.entries.map(validateEntry);
    setErrors(nextErrors);

    if (data.entries.length > 0 && nextErrors.every((entryErrors) => Object.keys(entryErrors).length === 0)) {
      onNext();
    }
  };

  const toggleDay = (entryIndex: number, dayIndex: number) => {
    const updated = [...data.entries];

    updated[entryIndex] = {
      ...updated[entryIndex],
      days: updated[entryIndex].days.map((day, index) => (index === dayIndex ? !day : day))
    };

    onChange({ entries: updated });

    if (hasAttemptedNext) {
      setErrors((previous) => {
        const next = [...previous];
        next[entryIndex] = validateEntry(updated[entryIndex]);
        return next;
      });
    }
  };

  const updateTime = (entryIndex: number, field: 'startTime' | 'endTime', value: string) => {
    const updated = [...data.entries];

    updated[entryIndex] = {
      ...updated[entryIndex],
      [field]: value
    };

    onChange({ entries: updated });

    if (hasAttemptedNext) {
      setErrors((previous) => {
        const next = [...previous];
        next[entryIndex] = validateEntry(updated[entryIndex]);
        return next;
      });
    }
  };

  const addEntry = () => {
    const newEntry = emptyEntry();

    onChange({ entries: [...data.entries, newEntry] });

    setErrors((previous) => [...previous, hasAttemptedNext ? validateEntry(newEntry) : {}]);
  };

  const removeEntry = (index: number) => {
    onChange({
      entries: data.entries.filter((_, i) => i !== index)
    });

    setErrors((previous) => previous.filter((_, i) => i !== index));
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '16px',
          marginBottom: '12px'
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: '0.96px'
            }}
          >
            DAYS OF WEEK
          </div>
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: '8px'
          }}
        >
          <div
            style={{
              width: '100px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: '0.96px'
            }}
          >
            START TIME
          </div>
          <div
            style={{
              width: '100px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: '0.96px'
            }}
          >
            END TIME
          </div>
          <div style={{ width: '30px' }} />
        </div>
      </div>

      {data.entries.map((entry, entryIndex) => (
        <div key={entryIndex} style={{ marginBottom: '12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px'
            }}
          >
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {DAYS.map((day, dayIndex) => (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={entry.days[dayIndex]}
                    onClick={() => toggleDay(entryIndex, dayIndex)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: errors[entryIndex]?.days ? '1px solid #dc2626' : 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: entry.days[dayIndex] ? 'var(--color-accent)' : '#e0e0e0',
                      color: entry.days[dayIndex] ? 'white' : '#666'
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>

              {errors[entryIndex]?.days && (
                <p
                  role="alert"
                  style={{
                    color: '#dc2626',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    maxWidth: '260px'
                  }}
                >
                  {errors[entryIndex].days}
                </p>
              )}
            </div>

            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start'
              }}
            >
              <div style={{ width: '100px' }}>
                <input
                  type="text"
                  aria-label="Start time"
                  aria-invalid={!!errors[entryIndex]?.startTime}
                  placeholder="7:00 AM"
                  value={entry.startTime}
                  onChange={(e) => updateTime(entryIndex, 'startTime', e.target.value)}
                  style={{
                    width: '100%',
                    height: '35px',
                    boxSizing: 'border-box',
                    border: `1px solid ${errors[entryIndex]?.startTime ? '#dc2626' : '#ccc'}`,
                    borderRadius: '4px',
                    padding: '0 8px',
                    fontSize: '0.75rem'
                  }}
                />
                {errors[entryIndex]?.startTime && (
                  <p
                    role="alert"
                    style={{
                      color: '#dc2626',
                      fontSize: '0.7rem',
                      marginTop: '4px',
                      width: '140px'
                    }}
                  >
                    {errors[entryIndex].startTime}
                  </p>
                )}
              </div>

              <div style={{ width: '100px' }}>
                <input
                  type="text"
                  aria-label="End time"
                  aria-invalid={!!errors[entryIndex]?.endTime}
                  placeholder="10:00 PM"
                  value={entry.endTime}
                  onChange={(e) => updateTime(entryIndex, 'endTime', e.target.value)}
                  style={{
                    width: '100%',
                    height: '35px',
                    boxSizing: 'border-box',
                    border: `1px solid ${errors[entryIndex]?.endTime ? '#dc2626' : '#ccc'}`,
                    borderRadius: '4px',
                    padding: '0 8px',
                    fontSize: '0.75rem'
                  }}
                />
                {errors[entryIndex]?.endTime && (
                  <p
                    role="alert"
                    style={{
                      color: '#dc2626',
                      fontSize: '0.7rem',
                      marginTop: '4px',
                      width: '140px'
                    }}
                  >
                    {errors[entryIndex].endTime}
                  </p>
                )}
              </div>

              <button
                type="button"
                aria-label={entryIndex === 0 ? 'Add hours row' : 'Remove hours row'}
                onClick={entryIndex === 0 ? addEntry : () => removeEntry(entryIndex)}
                style={{
                  width: '30px',
                  height: '35px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {entryIndex === 0 ? (
                  <span style={{ transform: 'scale(0.7)', display: 'flex' }}>
                    <PlusIcon />
                  </span>
                ) : (
                  <TrashCanIcon />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '24px'
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-disabled)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          BACK
        </button>

        <button
          type="button"
          onClick={handleNext}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          NEXT
        </button>
      </div>
    </div>
  );
};

export default LocationHoursStep;
